// pages/monitor/panels/peak/PeakCurve.js —— 削峰填谷 · 曲线报表（依据《云平台单站控制（削峰填谷）需求文档》p12-24）
// 关键差异：
//   #1 系统效率 = 总放电量 / 总充电量 × 100%（概览统计）
//   #2 实时运行时间轴「今日 0 点 ~ 次日 06:00」、最小颗粒度 1 分钟
//   #5 本类型有电池簇曲线
//   #6 电表报表默认范围：电表电量近 7 天 / 电表止度近 15 天（均不含今日）
//   #7 单体视图选项 = 分布图 / 表格 / 柱状图
//   #12 自定义曲线「全量单体导出最多 12h」（异步导出）
import React, { useMemo, useState } from 'react';
import { Segmented, Select, Space, Button, Table, DatePicker, message, Tag, Popconfirm } from 'antd';
import ScrollTabs from '../../../../components/ScrollTabs';
import { DownloadOutlined, PlusOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  ResponsiveContainer, ComposedChart, Line, Area, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ReferenceLine,
} from 'recharts';
import {
  getPowerCurve, getPcsCurve, getRackCurve, getExportList, getDayAhead, getDevices,
} from '../../../../config/stationData';
import {
  getIncomeRows, getEnergyRows, getMeterEnergyRows, getMeterReadingRows, getMinuteRealtime,
  getClusterCurveSeries, getCellCurveSeries, getTempReportRows, getVoltReportRows,
  TARIFF_SEGMENTS, CUSTOM_METRICS, CUSTOM_MAX, CUSTOM_EXPORT_LIMIT, SAMPLE_RATES, CUSTOM_TEMPLATES,
} from './data';
import './peak.css';

const TICK = { fill: 'rgba(255,255,255,0.5)', fontSize: 11 };
const TT = { background: '#0d1f44', border: '1px solid rgba(0,180,216,0.35)', borderRadius: 8, color: '#fff' };
const LEGEND = { wrapperStyle: { fontSize: 11, color: 'rgba(255,255,255,0.6)' }, iconSize: 8 };
const GRID = 'rgba(255,255,255,0.08)';
const transparentSummary = { background: 'transparent' };
const RANGE_OPTIONS = [{ value: '7d', label: '近 7 天' }, { value: 'm', label: '当月' }, { value: 'y', label: '当年' }];

const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);

// ---------------- 1. 概览统计（差异 #1） ----------------
const OverviewView = () => {
  const [kind, setKind] = useState('income');
  const [range, setRange] = useState('7d');
  const income = getIncomeRows();
  const energy = getEnergyRows();
  const rows = income.map((r, i) => ({
    day: r.day,
    income: r.actual,
    charge: energy[i].charge,
    discharge: energy[i].discharge,
    efficiency: energy[i].efficiency,
  }));
  const totalIncome = sum(rows, 'income');
  const totalCharge = sum(rows, 'charge');
  const totalDischarge = sum(rows, 'discharge');
  const sysEff = totalCharge ? +((totalDischarge / totalCharge) * 100).toFixed(1) : 0;

  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value={kind} onChange={setKind} options={[{ label: '储能收益', value: 'income' }, { label: '储能电量', value: 'energy' }]} />
        <span className="peak-toolbar-label">时间范围</span>
        <Segmented value={range} onChange={setRange} options={RANGE_OPTIONS} />
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
      </div>
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        {kind === 'income' ? (
          <div className="kpi-card"><div className="kpi-label">总收益</div><div className="kpi-value">{totalIncome.toLocaleString()}<span className="unit">元</span></div><div className="kpi-foot">日期范围内日收益加和</div></div>
        ) : (
          <>
            <div className="kpi-card"><div className="kpi-label">总充电量</div><div className="kpi-value">{totalCharge.toLocaleString()}<span className="unit">kWh</span></div><div className="kpi-foot">日期范围内日充电量加和</div></div>
            <div className="kpi-card"><div className="kpi-label">总放电量</div><div className="kpi-value">{totalDischarge.toLocaleString()}<span className="unit">kWh</span></div><div className="kpi-foot">日期范围内日放电量加和</div></div>
            <div className="kpi-card"><div className="kpi-label">系统效率</div><div className="kpi-value">{sysEff}<span className="unit">%</span></div><div className="kpi-foot">总放电量 / 总充电量 × 100%</div></div>
          </>
        )}
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="day" tick={TICK} axisLine={false} tickLine={false} />
          <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={56} />
          {kind === 'energy' ? <YAxis yAxisId="r" orientation="right" domain={[80, 100]} tick={TICK} axisLine={false} tickLine={false} width={44} /> : null}
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          {kind === 'income'
            ? <Bar yAxisId="l" dataKey="income" name="日收益 元" fill="#4dd8ff" radius={[4, 4, 0, 0]} />
            : (
              <>
                <Bar yAxisId="l" dataKey="charge" name="日充电量 kWh" fill="#8b7cf6" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="l" dataKey="discharge" name="日放电量 kWh" fill="#52e8b0" radius={[4, 4, 0, 0]} />
                <Line yAxisId="r" type="linear" dataKey="efficiency" name="系统效率 %" stroke="#f4c542" strokeWidth={2} dot />
              </>
            )}
        </ComposedChart>
      </ResponsiveContainer>
      <div className="peak-note">柱形图数值分别取自「收益报表」的日收益、「电量报表」的日充电量/放电量与综合效率。</div>
    </div>
  );
};

// ---------------- 2. 收益报表 ----------------
const IncomeView = ({ station }) => {
  const [scope, setScope] = useState('all');
  const [rangeKind, setRangeKind] = useState('segment');
  const rows = getIncomeRows();
  const dev = getDevices(station.id);
  const cols = [
    { title: '日期', dataIndex: 'day', fixed: 'left', width: 90 },
    ...TARIFF_SEGMENTS.map((s) => ({ title: `充电成本·${s.label}(元)`, dataIndex: `c${s.key}`, width: 118 })),
    ...TARIFF_SEGMENTS.map((s) => ({ title: `放电收益·${s.label}(元)`, dataIndex: `d${s.key}`, width: 118 })),
    { title: '实际收益(元)', dataIndex: 'actual', width: 110, render: (v) => <b style={{ color: '#52e8b0' }}>{v}</b> },
  ];
  const priceSeries = getDayAhead().plan;
  return (
    <div>
      <div className="peak-toolbar">
        <span className="peak-toolbar-label">统计范围</span>
        <Segmented value={scope} onChange={setScope} options={[{ label: '全部电表', value: 'all' }].concat(dev.meters.map((m) => ({ label: m.name, value: m.id })))} />
        <span className="peak-toolbar-label">时段划分</span>
        <Segmented value={rangeKind} onChange={setRangeKind} options={[{ label: '按时段', value: 'segment' }, { label: '按 15 分钟（日前电价）', value: '15min' }]} />
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
        <Button size="small" className="panel-title-btn" icon={<DownloadOutlined />} onClick={() => message.success('收益报表导出任务已提交（异步）')}>导出</Button>
      </div>
      <Table
        size="small" rowKey="day" dataSource={rows} scroll={{ x: 1400 }} pagination={false}
        columns={cols}
        summary={() => (
          <Table.Summary.Row style={transparentSummary}>
            <Table.Summary.Cell index={0}><b>合计</b></Table.Summary.Cell>
            {cols.slice(1, -1).map((c, i) => (
              <Table.Summary.Cell index={i + 1} key={c.dataIndex}>{sum(rows, c.dataIndex).toFixed(1)}</Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={cols.length - 1}><b style={{ color: '#52e8b0' }}>{sum(rows, 'actual').toFixed(1)}</b></Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
      {rangeKind === '15min' ? (
        <>
          <div className="panel-title" style={{ marginTop: 16 }}>日前电价<small>15 分钟颗粒度 · 阶梯电价</small></div>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={priceSeries} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
              <XAxis dataKey="clock" interval={7} tick={TICK} axisLine={false} tickLine={false} />
              <YAxis tick={TICK} axisLine={false} tickLine={false} width={44} />
              <Tooltip contentStyle={TT} />
              <Line type="stepAfter" dataKey="price" name="日前电价 元/kWh" stroke="#f4c542" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      ) : null}
    </div>
  );
};

// ---------------- 3. 电量报表 ----------------
const EnergyView = () => {
  const rows = getEnergyRows();
  const cols = [
    { title: '日期', dataIndex: 'day', fixed: 'left', width: 90 },
    ...TARIFF_SEGMENTS.map((s) => ({ title: `充电量·${s.label}(kWh)`, dataIndex: `charge_${s.key}`, width: 118 })),
    ...TARIFF_SEGMENTS.map((s) => ({ title: `放电量·${s.label}(kWh)`, dataIndex: `discharge_${s.key}`, width: 118 })),
    { title: '综合效率(%)', dataIndex: 'efficiency', width: 110, render: (v) => <b style={{ color: '#f4c542' }}>{v}</b> },
  ];
  return (
    <div>
      <div className="peak-toolbar">
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
        <Button size="small" className="panel-title-btn" icon={<DownloadOutlined />} onClick={() => message.success('电量报表导出任务已提交（异步）')}>导出</Button>
      </div>
      <Table
        size="small" rowKey="day" dataSource={rows} scroll={{ x: 1400 }} pagination={false}
        columns={cols}
        summary={() => (
          <Table.Summary.Row style={transparentSummary}>
            <Table.Summary.Cell index={0}><b>合计</b></Table.Summary.Cell>
            {cols.slice(1, -1).map((c, i) => (
              <Table.Summary.Cell index={i + 1} key={c.dataIndex}>{sum(rows, c.dataIndex)}</Table.Summary.Cell>
            ))}
            <Table.Summary.Cell index={cols.length - 1}>
              <b style={{ color: '#f4c542' }}>{((sum(rows, 'discharge') / sum(rows, 'charge')) * 100).toFixed(1)}</b>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </div>
  );
};

// ---------------- 4. 电表报表（差异 #6） ----------------
const MeterReportView = () => {
  const [kind, setKind] = useState('energy');
  const energyRows = getMeterEnergyRows();
  const readingRows = getMeterReadingRows();
  const isEnergy = kind === 'energy';
  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value={kind} onChange={setKind} options={[{ label: '电表电量', value: 'energy' }, { label: '电表止度', value: 'reading' }]} />
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
        <span className="peak-toolbar-label">{isEnergy ? '默认近 7 天（不含今日）' : '默认近 15 天（不含今日）'}</span>
        <Button size="small" className="panel-title-btn" icon={<DownloadOutlined />} onClick={() => message.success(`电表${isEnergy ? '电量' : '止度'}报表导出任务已提交`)}>导出</Button>
      </div>
      {isEnergy ? (
        <Table
          size="small" rowKey="key" pagination={false} dataSource={energyRows}
          columns={[
            { title: '日期', dataIndex: 'day' },
            { title: '电表', dataIndex: 'meter' },
            { title: '尖时电量(kWh)', dataIndex: 'sharp' },
            { title: '峰时电量(kWh)', dataIndex: 'peak' },
            { title: '平时电量(kWh)', dataIndex: 'flat' },
            { title: '谷时电量(kWh)', dataIndex: 'valley' },
            { title: '深谷电量(kWh)', dataIndex: 'deep' },
            { title: '分时电量来源', dataIndex: 'source', render: (v) => <Tag color={v === '电表分时' ? 'cyan' : 'geekblue'}>{v}</Tag> },
            { title: '综合倍率', dataIndex: 'ratio' },
          ]}
        />
      ) : (
        <Table
          size="small" rowKey="key" pagination={false} dataSource={readingRows}
          columns={[
            { title: '日期', dataIndex: 'day' },
            { title: '电表', dataIndex: 'meter' },
            { title: '抄表时刻', dataIndex: 'time' },
            { title: '00:00 止度（乘综合倍率）', dataIndex: 'reading' },
            { title: '综合倍率', dataIndex: 'ratio' },
          ]}
        />
      )}
      <div className="peak-note">{isEnergy ? '电表电量默认查询近 7 天（不含今日），展示各电表分时电量与来源。' : '电表止度默认查询近 15 天（不含今日），展示 00:00 止度与设置的综合倍率。'}</div>
    </div>
  );
};

// ---------------- 5. 功率曲线 ----------------
const PowerView = ({ station }) => {
  const minute = getMinuteRealtime(station);
  const rows = useMemo(() => minute.filter((_, i) => i % 5 === 0).map((r) => ({
    clock: r.clock, grid: r.grid, load: r.load, storage: r.storage,
    pv: station.has.pv ? r.pv : null, soc: r.soc,
  })), [minute, station.has.pv]);
  const limit = getPowerCurve(station.id)[0].demandLimit;
  return (
    <div>
      <div className="peak-toolbar">
        <DatePicker.RangePicker size="small" style={{ width: 260 }} />
        <span className="peak-toolbar-label">X 轴：今日 0 点 ~ 次日 06:00（最小颗粒度 1 分钟，此处按 5 分钟抽稀展示）</span>
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <defs>
            <linearGradient id="pkSoc3" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#8b7cf6" stopOpacity={0.4} />
              <stop offset="95%" stopColor="#8b7cf6" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="clock" interval={23} tick={TICK} axisLine={false} tickLine={false} />
          <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={48} />
          <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} width={38} />
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          <ReferenceLine yAxisId="l" y={0} stroke="rgba(255,255,255,0.35)" />
          <ReferenceLine yAxisId="l" y={limit} stroke="#f4c542" strokeDasharray="5 5" label={{ value: `需量限制 ${limit} kW`, fill: '#f4c542', fontSize: 11 }} />
          <Line yAxisId="l" type="stepAfter" dataKey="grid" name="电网功率" stroke="#4dd8ff" dot={false} strokeWidth={2} />
          <Line yAxisId="l" type="stepAfter" dataKey="load" name="负载功率" stroke="#ff5a7f" dot={false} strokeWidth={2} />
          <Line yAxisId="l" type="stepAfter" dataKey="storage" name="储能功率" stroke="#52e8b0" dot={false} strokeWidth={2} />
          {station.has.pv ? <Line yAxisId="l" type="stepAfter" dataKey="pv" name="光伏功率" stroke="#f4c542" dot={false} strokeWidth={2} /> : null}
          <Area yAxisId="r" type="linear" dataKey="soc" name="SOC %" stroke="#8b7cf6" fill="url(#pkSoc3)" strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="peak-note">SOC 量纲不同，使用右侧轴。</div>
    </div>
  );
};

// ---------------- 6/7/8. 设备曲线（PCS / 电池堆 / 电池簇） ----------------
const PcsCurveView = ({ station }) => {
  const [span, setSpan] = useState('compare');
  const [metric, setMetric] = useState('power');
  const dev = getDevices(station.id);
  const base = getPcsCurve();
  const rows = base.map((r, i) => ({
    clock: span === 'compare' ? `${String(i).padStart(2, '0')}:00` : `${i + 11}日`,
    powerToday: r.dcPower,
    powerPrev: r.dcPowerYesterday,
    tempToday: r.today,
    tempPrev: r.yesterday,
    voltageToday: r.aVoltage,
    voltagePrev: +(r.aVoltage - (i % 3 === 0 ? 0.8 : 0.2)).toFixed(1),
    currentToday: +r.aCurrent.toFixed(1),
    currentPrev: +(Math.abs(r.dcPowerYesterday) > 0 ? 40 + Math.abs(r.dcPowerYesterday) * 0.55 : 8).toFixed(1),
  }));
  const meta = {
    power: ['功率 kW', 'powerToday', 'powerPrev'],
    temp: ['温度 ℃', 'tempToday', 'tempPrev'],
    aVoltage: ['三相电压 V', 'voltageToday', 'voltagePrev'],
    aCurrent: ['三相电流 A', 'currentToday', 'currentPrev'],
  }[metric];
  const lineType = metric === 'power' || metric === 'aCurrent' ? 'stepAfter' : 'linear';
  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value={span} onChange={setSpan} options={[{ label: '对比（近 2 天）', value: 'compare' }, { label: '趋势（近 7 天）', value: 'trend' }]} />
        <span className="peak-toolbar-label">设备</span>
        <Select size="small" defaultValue={dev.pcs[0].id} style={{ width: 140 }} options={dev.pcs.map((p) => ({ value: p.id, label: p.name }))} />
        <Segmented value={metric} onChange={setMetric} options={[{ label: '功率', value: 'power' }, { label: '温度', value: 'temp' }, { label: '三相电压', value: 'aVoltage' }, { label: '三相电流', value: 'aCurrent' }]} />
        <DatePicker.RangePicker size="small" style={{ width: 240 }} />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} />
          <YAxis tick={TICK} axisLine={false} tickLine={false} width={50} />
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
          <Line type={lineType} dataKey={meta[1]} name={`今日 · ${meta[0]}`} stroke="#4dd8ff" dot={false} strokeWidth={2} />
          <Line type={lineType} dataKey={meta[2]} name={`对比日 · ${meta[0]}`} stroke="#8b7cf6" dot={false} strokeWidth={2} strokeDasharray="4 4" />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

const RackCurveView = ({ station }) => {
  const [span, setSpan] = useState('compare');
  const [metric, setMetric] = useState('soc');
  const dev = getDevices(station.id);
  const base = getRackCurve();
  const rows = base.map((r, i) => ({
    clock: span === 'compare' ? `${String(i * 3).padStart(2, '0')}:00` : `${i + 11}日`,
    socToday: r.soc, socPrev: +(r.soc - 1.8 + Math.sin(i) * 1.2).toFixed(1),
    voltageToday: r.voltage, voltagePrev: +(r.voltage - 1.5 + Math.cos(i) * 1.4).toFixed(1),
    currentToday: +r.current.toFixed(1), currentPrev: +(r.current - 5 + Math.sin(i) * 4).toFixed(1),
    tempToday: r.tempMax, tempPrev: +(r.tempMax - 0.6 + Math.cos(i) * 0.5).toFixed(1),
  }));
  const meta = {
    soc: ['SOC %', 'socToday', 'socPrev'],
    voltage: ['电压 V', 'voltageToday', 'voltagePrev'],
    current: ['电流 A', 'currentToday', 'currentPrev'],
    temp: ['温度 ℃', 'tempToday', 'tempPrev'],
  }[metric];
  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value={span} onChange={setSpan} options={[{ label: '对比（近 2 天）', value: 'compare' }, { label: '趋势（近 7 天）', value: 'trend' }]} />
        <span className="peak-toolbar-label">设备</span>
        <Select size="small" defaultValue={dev.racks[0].id} style={{ width: 150 }} options={dev.racks.map((r) => ({ value: r.id, label: r.name }))} />
        <Segmented value={metric} onChange={setMetric} options={[{ label: 'SOC', value: 'soc' }, { label: '电压', value: 'voltage' }, { label: '电流', value: 'current' }, { label: '温度', value: 'temp' }]} />
        <DatePicker.RangePicker size="small" style={{ width: 240 }} />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} />
          <YAxis domain={metric === 'soc' ? [0, 100] : ['auto', 'auto']} tick={TICK} axisLine={false} tickLine={false} width={50} />
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
          <Line type="linear" dataKey={meta[1]} name={`今日 · ${meta[0]}`} stroke="#52e8b0" dot={false} strokeWidth={2} />
          <Line type="linear" dataKey={meta[2]} name={`对比日 · ${meta[0]}`} stroke="#f4c542" dot={false} strokeWidth={2} strokeDasharray="4 4" />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="peak-note">电池堆曲线支持对比（近 2 天）/ 趋势（近 7 天），并可切换设备与统计时间段。</div>
    </div>
  );
};

const ClusterCurveView = ({ station }) => {
  const [span, setSpan] = useState('compare');
  const [metric, setMetric] = useState('soc');
  const dev = getDevices(station.id);
  const rows = getClusterCurveSeries(span);
  const meta = { soc: ['SOC %', [0, 100]], voltage: ['电压 V', ['auto', 'auto']], current: ['电流 A', ['auto', 'auto']], temp: ['温度 ℃', ['auto', 'auto']] }[metric];
  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value={span} onChange={setSpan} options={[{ label: '对比（近 2 天）', value: 'compare' }, { label: '趋势（近 7 天）', value: 'trend' }]} />
        <span className="peak-toolbar-label">设备 / 簇</span>
        <Select size="small" defaultValue="RACK-01 · 1 号簇" style={{ width: 190 }}
          options={dev.racks.map((r) => ({ value: r.id, label: `${r.name} · 1 号簇` }))} />
        <Segmented value={metric} onChange={setMetric} options={[{ label: 'SOC', value: 'soc' }, { label: '电压', value: 'voltage' }, { label: '电流', value: 'current' }, { label: '温度', value: 'temp' }]} />
        <DatePicker.RangePicker size="small" style={{ width: 240 }} />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} interval={span === 'trend' ? 0 : 2} />
          <YAxis domain={meta[1]} tick={TICK} axisLine={false} tickLine={false} width={50} />
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          <ReferenceLine y={0} stroke="rgba(255,255,255,0.35)" />
          <Line type="linear" dataKey={metric} name={`电池簇 · ${meta[0]}`} stroke="#8b7cf6" strokeWidth={2} dot={span === 'trend'} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="peak-note">差异 #5：削峰填谷类型包含「电池簇曲线」，支持切换设备与统计时间段。</div>
    </div>
  );
};

// ---------------- 9. 单体曲线 ----------------
const CellCurveView = ({ station }) => {
  const [pick, setPick] = useState(1);
  const dev = getDevices(station.id);
  const rows = getCellCurveSeries(pick);
  return (
    <div>
      <div className="peak-toolbar">
        <Segmented value="trend" options={[{ label: '趋势（近 7 天）', value: 'trend' }]} />
        <span className="peak-toolbar-label">设备</span>
        <Select size="small" defaultValue={dev.racks[0].id} style={{ width: 150 }} options={dev.racks.map((r) => ({ value: r.id, label: r.name }))} />
        <span className="peak-toolbar-label">单体</span>
        <Select size="small" value={pick} onChange={setPick} style={{ width: 140 }}
          options={[1, 26, 52, 78, 104].map((n) => ({ value: n, label: `${n} 号单体` }))} />
        <DatePicker.RangePicker size="small" style={{ width: 240 }} />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
          <XAxis dataKey="clock" tick={TICK} axisLine={false} tickLine={false} />
          <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={50} />
          <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} width={38} />
          <Tooltip contentStyle={TT} />
          <Legend {...LEGEND} />
          <ReferenceLine yAxisId="l" y={0} stroke="rgba(255,255,255,0.35)" />
          <Line yAxisId="l" type="linear" dataKey="voltage" name="电压 V" stroke="#4dd8ff" dot strokeWidth={2} />
          <Line yAxisId="l" type="linear" dataKey="temp" name="温度 ℃" stroke="#ff5a7f" dot strokeWidth={2} />
          <Line yAxisId="r" type="linear" dataKey="soc" name="SOC %" stroke="#52e8b0" dot strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
      <div className="peak-note">单体曲线为所选单体最近 7 天的电压、温度、SOC；电压/温度与 SOC 量纲不同，采用左右双轴。</div>
    </div>
  );
};

// ---------------- 10/11. 电池温度 / 电压报表 ----------------
const TempReportView = () => (
  <Table size="small" rowKey="key" pagination={false} dataSource={getTempReportRows()}
    columns={[
      { title: '电池簇', dataIndex: 'cluster' },
      { title: '整点时刻', dataIndex: 'time' },
      { title: '最高单体温度(℃)', dataIndex: 'maxVal' },
      { title: '最高单体 ID', dataIndex: 'maxCell' },
      { title: '最低单体温度(℃)', dataIndex: 'minVal' },
      { title: '最低单体 ID', dataIndex: 'minCell' },
    ]}
  />
);

const VoltReportView = () => (
  <div>
    <Table size="small" rowKey="key" pagination={false} dataSource={getVoltReportRows()}
      columns={[
        { title: '电池簇', dataIndex: 'cluster' },
        { title: '整点时刻', dataIndex: 'time' },
        { title: '最高单体电压(V)', dataIndex: 'maxVal' },
        { title: '最高单体 ID', dataIndex: 'maxCell' },
        { title: '最低单体电压(V)', dataIndex: 'minVal' },
        { title: '最低单体 ID', dataIndex: 'minCell' },
        { title: '极差(V)', dataIndex: 'diff' },
      ]}
    />
    <div className="peak-note">整点时列出该簇下所有电池的电压，给出最高 / 最低单体及其 ID 与极差。</div>
  </div>
);

// ---------------- 12. 自定义曲线（差异 #12） ----------------
const CustomView = ({ station, exports, setExports }) => {
  const [added, setAdded] = useState(['storage', 'soc']);
  const [mode, setMode] = useState('realtime');
  const [rate, setRate] = useState('1 分钟');
  const [template, setTemplate] = useState('默认模板');
  const minute = getMinuteRealtime(station);
  const onExport = () => {
    setExports([{ key: Date.now(), name: `自定义曲线_${rate}.xlsx`, type: `自定义曲线（${added.length} 测点）`, time: '刚刚', state: '处理中', size: '-' }, ...exports]);
    message.success('导出任务已创建（异步），可在「导出列表」查询');
  };
  const rows = useMemo(() => minute.filter((_, i) => i % 15 === 0), [minute]);
  const addMetric = (key) => {
    if (added.includes(key)) { message.info('该数据项已添加'); return; }
    if (added.length >= CUSTOM_MAX) { message.warning(`最多选择 ${CUSTOM_MAX} 项数据`); return; }
    setAdded([...added, key]);
  };
  return (
    <div className="grid-2">
      <div className="panel">
        <div className="panel-title">添加数据<small>最多 {CUSTOM_MAX} 项</small></div>
        <Space direction="vertical" style={{ width: '100%' }} size={6}>
          {CUSTOM_METRICS.map((m) => (
            <div key={m.key} className="peak-form-row" style={{ marginBottom: 0 }}>
              <span className="peak-form-label">{m.label}（{m.unit}）</span>
              <Button size="small" icon={<PlusOutlined />} disabled={added.includes(m.key)} onClick={() => addMetric(m.key)}>添加</Button>
            </div>
          ))}
        </Space>
        <div className="panel-title" style={{ marginTop: 16 }}>已选数据（{added.length}/{CUSTOM_MAX}）</div>
        <div className="peak-toolbar">
          {added.map((k) => {
            const m = CUSTOM_METRICS.find((x) => x.key === k);
            return <Tag key={k} closable onClose={() => setAdded(added.filter((x) => x !== k))} color="cyan">{m.label}</Tag>;
          })}
        </div>
        <Space>
          <Select size="small" value={template} onChange={setTemplate} style={{ width: 160 }} options={CUSTOM_TEMPLATES.map((t) => ({ value: t, label: t }))} />
          <Button size="small" icon={<SaveOutlined />} onClick={() => message.success(`已保存为模板「${template}」（演示）`)}>保存模板</Button>
        </Space>
      </div>

      <div className="panel">
        <div className="panel-title">自定义曲线<small>最多 {CUSTOM_MAX} 项 · 采样可调</small></div>
        <div className="peak-toolbar">
          <Segmented value={mode} onChange={setMode} options={[{ label: '实时查询（今日 0 点 ~ 此刻）', value: 'realtime' }, { label: '时段查询（精确到秒）', value: 'period' }]} />
        </div>
        {mode === 'period' ? (
          <div className="peak-toolbar">
            <DatePicker.RangePicker
              size="small" showTime={{ format: 'HH:mm:ss' }} format="YYYY-MM-DD HH:mm:ss" style={{ width: 420 }} />
          </div>
        ) : <div className="peak-note" style={{ marginTop: 0 }}>实时查询范围默认「今日 0 点 ~ 此刻」。</div>}
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={rows} margin={{ top: 10, right: 16, bottom: 0, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
            <XAxis dataKey="clock" interval={19} tick={TICK} axisLine={false} tickLine={false} />
            <YAxis yAxisId="l" tick={TICK} axisLine={false} tickLine={false} width={50} />
            <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={TICK} axisLine={false} tickLine={false} width={38} />
            <Tooltip contentStyle={TT} />
            <Legend {...LEGEND} />
            <ReferenceLine yAxisId="l" y={0} stroke="rgba(255,255,255,0.35)" />
            {added.filter((k) => k !== 'soc').map((k) => {
              const m = CUSTOM_METRICS.find((x) => x.key === k);
              const isPower = m && (m.unit === 'kW' || m.unit === 'kVar');
              return <Line key={k} yAxisId="l" type={isPower ? 'stepAfter' : 'linear'} dataKey={k} name={`${m.label}（${m.unit}）`} stroke={m.color} dot={false} strokeWidth={2} />;
            })}
            {added.includes('soc') ? <Line yAxisId="r" type="linear" dataKey="soc" name="SOC（%）" stroke="#8b7cf6" dot={false} strokeWidth={2} /> : null}
          </ComposedChart>
        </ResponsiveContainer>
        <div className="peak-toolbar" style={{ marginTop: 10 }}>
          <span className="peak-toolbar-label">采样频率</span>
          <Select size="small" value={rate} onChange={setRate} style={{ width: 120 }} options={SAMPLE_RATES.map((r) => ({ value: r, label: r }))} />
          <Popconfirm title="导出" description={CUSTOM_EXPORT_LIMIT} onConfirm={onExport} okText="仍要导出" cancelText="取消">
            <Button size="small" className="panel-title-btn" icon={<DownloadOutlined />}>导出</Button>
          </Popconfirm>
        </div>
        <div className="peak-note">{CUSTOM_EXPORT_LIMIT}；导出为异步导出，结果在「导出列表」查看。</div>
      </div>
    </div>
  );
};

// ---------------- 13. 导出列表 ----------------
// 依据需求文档 p24：导出时间 / 文件名 / 文件大小MB / 数据范围 / 数据频率/s / 任务状态 / 操作
const toSizeMB = (size) => {
  const kb = parseFloat(size);
  return Number.isFinite(kb) ? (kb / 1024).toFixed(3) : '—';
};
const ExportListView = ({ exports, setExports }) => {
  const refresh = () => {
    message.success('已刷新任务状态');
    setExports(exports.map((e) => e.state === '处理中' ? { ...e, state: '已完成', size: `${(Math.random() * 12).toFixed(1)} KB` } : e));
  };
  const rows = exports.map((e) => ({
    ...e,
    sizeMB: toSizeMB(e.size),
    range: `${e.time.slice(0, 10)} 00:00:00\n${e.time}:00`,
    freq: 60,
    result: e.state === '已完成' ? '成功' : e.state,
  }));
  return (
    <div>
      <div className="panel-title">
        <span>导出列表</span>
        <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>刷新</Button>
      </div>
      <Table size="small" rowKey="key" dataSource={rows}
        pagination={{ pageSize: 8, size: 'small' }} scroll={{ x: 660 }}
        columns={[
          { title: '导出时间', dataIndex: 'time', width: 105, ellipsis: true },
          { title: '文件名称', dataIndex: 'name', width: 135, ellipsis: true },
          { title: '文件大小MB', dataIndex: 'sizeMB', width: 80 },
          { title: '数据范围', dataIndex: 'range', width: 128, render: (v) => <span style={{ whiteSpace: 'pre-line' }}>{v}</span> },
          { title: '数据频率/s', dataIndex: 'freq', width: 72 },
          { title: '任务状态', dataIndex: 'result', width: 70, render: (v) => <Tag color={v === '成功' ? 'green' : 'processing'}>{v}</Tag> },
          { title: '操作', width: 70, render: (_, r) => r.state === '已完成'
            ? <Button size="small" type="link" onClick={() => message.success('下载已开始（演示）')}>下载</Button>
            : <span style={{ color: 'rgba(255,255,255,0.35)' }}>—</span> },
        ]}
      />
    </div>
  );
};

const PeakCurve = ({ station }) => {
  const [exports, setExports] = useState(() => getExportList());
  const items = [
    { key: 'overview', label: '概览统计', children: <OverviewView /> },
    { key: 'income', label: '收益报表', children: <IncomeView station={station} /> },
    { key: 'energy', label: '电量报表', children: <EnergyView /> },
    { key: 'meter', label: '电表报表', children: <MeterReportView /> },
    { key: 'power', label: '功率曲线', children: <PowerView station={station} /> },
    { key: 'pcs', label: 'PCS 曲线', children: <PcsCurveView station={station} /> },
    { key: 'rack', label: '电池堆曲线', children: <RackCurveView station={station} /> },
    { key: 'cluster', label: '电池簇曲线', children: <ClusterCurveView station={station} /> },
    { key: 'cell', label: '单体曲线', children: <CellCurveView station={station} /> },
    { key: 'temp', label: '电池温度报表', children: <TempReportView /> },
    { key: 'volt', label: '电池电压报表', children: <VoltReportView /> },
    { key: 'custom', label: '自定义曲线', children: <CustomView station={station} exports={exports} setExports={setExports} /> },
    { key: 'export', label: '导出列表', children: <ExportListView exports={exports} setExports={setExports} /> },
  ];
  // 子页切换对齐光储充「控制中心」顶部胶囊 Tabs
  return <ScrollTabs items={items} />;
};

export default PeakCurve;
